// Client for graham's `/outreach` router. Same axios + applyAuthHeaders
// pattern as features/apcm-ops/api/apcm-ops.api.ts. The generic `/campaigns`
// endpoints (list, get, enrollments, per-enrollment pause/resume) stay in
// lib/api.ts and are reused from there.

import { applyAuthHeaders } from "@/lib/authHeaders"
import axios, { AxiosInstance, AxiosRequestConfig } from "axios"
import { inboxRequestParams } from "../lib/inbox"
import type {
  ArmResults,
  BookCallRequest,
  BookCallResult,
  CampaignFunnel,
  CancelCallResult,
  ContactPreference,
  ContactPreferenceUpdate,
  InboxItem,
  InboxSource,
  InboxStatus,
  PauseRequest,
  PauseScope,
  PauseState,
  ReliabilityReport,
  ResolveInboxRequest,
  ResolveInboxResult,
  ResumeRequest,
  SavePreferenceResult,
  ScheduledCallInfo,
} from "../types/outreach.types"

const axiosInstance: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:8000",
  headers: { "Content-Type": "application/json" },
})

axiosInstance.interceptors.request.use(async (config) => {
  await applyAuthHeaders(config)
  return config
})

async function apiCall<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await axiosInstance(config)
  return response.data
}

/** Best human-readable message from an axios error. */
export function errorMessage(err: any): string {
  const detail = err?.response?.data?.detail
  if (typeof detail === "string") return detail
  if (Array.isArray(detail)) return detail.map((d: any) => d?.msg ?? String(d)).join("; ")
  if (detail && typeof detail.message === "string") return detail.message
  return err?.message || "Unknown error"
}

/** HTTP status of an axios error, if it got a response. */
export function errorStatus(err: any): number | null {
  return typeof err?.response?.status === "number" ? err.response.status : null
}

// ─── Pause / resume ──────────────────────────────────────────────

export function getPause(scope: PauseScope, targetId: string) {
  return apiCall<PauseState>({
    method: "GET",
    url: `/outreach/pause`,
    params: { scope, target_id: targetId },
  })
}

export function pause(body: PauseRequest) {
  return apiCall<PauseState>({ method: "POST", url: `/outreach/pause`, data: body })
}

export function resume(body: ResumeRequest) {
  return apiCall<PauseState>({ method: "POST", url: `/outreach/resume`, data: body })
}

// ─── Contact preference ──────────────────────────────────────────

/** The server answers {preference: ContactPreference | null}. */
export async function getContactPreference(patientId: string): Promise<ContactPreference | null> {
  const data = await apiCall<{ preference: ContactPreference | null }>({
    method: "GET",
    url: `/outreach/patients/${patientId}/contact-preference`,
  })
  return data?.preference ?? null
}

export function putContactPreference(patientId: string, body: ContactPreferenceUpdate) {
  return apiCall<SavePreferenceResult>({
    method: "PUT",
    url: `/outreach/patients/${patientId}/contact-preference`,
    data: body,
  })
}

// ─── Booking ─────────────────────────────────────────────────────

/** The patient's one pending (ENABLED) call, or null. */
export function getPendingCall(patientId: string) {
  return apiCall<ScheduledCallInfo | null>({
    method: "GET",
    url: `/outreach/patients/${patientId}/call`,
  })
}

export function bookCall(patientId: string, body: BookCallRequest) {
  return apiCall<BookCallResult>({
    method: "POST",
    url: `/outreach/patients/${patientId}/call`,
    data: body,
  })
}

export function cancelCall(patientId: string) {
  return apiCall<CancelCallResult>({
    method: "DELETE",
    url: `/outreach/patients/${patientId}/call`,
  })
}

// ─── Reply inbox ─────────────────────────────────────────────────

/** The server answers {items: InboxItem[]}. status: an ActionItem status or "all"; source: one outreach source. */
export async function getInbox(
  orgId: string,
  opts: { status?: InboxStatus; source?: InboxSource | null } = {},
): Promise<InboxItem[]> {
  const data = await apiCall<{ items: InboxItem[] }>({
    method: "GET",
    url: `/outreach/org/${orgId}/inbox`,
    params: inboxRequestParams(opts),
  })
  return data?.items ?? []
}

/**
 * The server answers 200 with resolved: false when the action was refused
 * (e.g. booking outside policy); that is thrown here with the server's reason
 * so callers never report a refused action as done.
 */
export async function resolveInboxItem(actionItemId: string, body: ResolveInboxRequest): Promise<ResolveInboxResult> {
  const result = await apiCall<ResolveInboxResult>({
    method: "POST",
    url: `/outreach/inbox/${actionItemId}/resolve`,
    data: body,
  })
  if (!result?.resolved) {
    throw new Error(result?.error || "The action was not carried out")
  }
  return result
}

// ─── Reliability ─────────────────────────────────────────────────

export function getReliability(orgId: string, days = 7) {
  return apiCall<ReliabilityReport>({
    method: "GET",
    url: `/outreach/org/${orgId}/reliability`,
    params: { days },
  })
}

// ─── Campaign reads (create/preview/launch are gone: the clinic program replaced them) ───

export function getCampaignArms(orgId: string, campaignId: string) {
  return apiCall<ArmResults>({
    method: "GET",
    url: `/outreach/org/${orgId}/campaigns/${campaignId}/arms`,
  })
}

export function getAbResults(orgId: string, tag: string) {
  return apiCall<ArmResults>({ method: "GET", url: `/outreach/org/${orgId}/ab-results`, params: { tag } })
}

export function getCampaignFunnel(orgId: string, campaignId: string) {
  return apiCall<CampaignFunnel>({
    method: "GET",
    url: `/outreach/org/${orgId}/campaigns/${campaignId}/funnel`,
  })
}
